/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { forwardRef } from "react";
import { PALETTE } from "styles/base/variables";

export interface BaseInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  variant?: "filled" | "outlined" | "standard";
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
}

const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>(
  function BaseInput(
    {
      variant: _variant = "outlined",
      startAdornment,
      endAdornment,
      ...props
    }: BaseInputProps,
    ref
  ) {
    const defaultCss = `
      padding: 14px;
      font-size: 14px;
      transition: background 0.3s;
    `;

    const filledCss = `
      ${defaultCss}
    `;

    const outlinedCss = `
      ${defaultCss}
      background-color: ${PALETTE.light};
      border: 1px solid ${PALETTE.border};
      border-radius: 4px;

      ${
        props.disabled &&
        `
          background-color: ${PALETTE.backgroundDisabled};
        `
      }
    `;

    const standardCss = `
      ${defaultCss}
      border-top: none;
      border-right: none;
      border-left: none;
      border-radius: 4px 4px 0 0;
      border-width: 1px;
    `;

    const cssRenderer = (variant: string) => {
      switch (variant) {
        case "filled":
          return filledCss;
        case "outlined":
          return outlinedCss;
        case "standard":
          return standardCss;
        default:
          return outlinedCss;
      }
    };

    return (
      <div
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        {startAdornment && startAdornment}
        <input
          ref={ref}
          css={css`
            ${cssRenderer(_variant)}
          `}
          {...props}
        />
        {endAdornment && endAdornment}
      </div>
    );
  }
);

export default BaseInput;
